import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchData } from 'services/api/useFetchData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import FeatureBadge from 'components/billing/FeatureBadge';
import { CUSTOM_FIELDS_SAVED_EVENT } from 'components/forms/custom_fields/CustomFieldTypes';

const CustomFieldUsageCounter: React.FC = () => {
    const { t } = useTranslation();
    const { activeFeatures } = useAuthContext();

    // Fetch the number of active custom fields and the limit of the environment
    const { response: usage, refetch } = useFetchData({
        apiUrl: 'get_custom_field_usage',
        params: useMemo(() => ({}), []),
    });

    // Refetch the usage when custom fields are saved somewhere on the page
    useEffect(() => {
        const handleSaved = () => { refetch(); };
        window.addEventListener(CUSTOM_FIELDS_SAVED_EVENT, handleSaved);
        return () => window.removeEventListener(CUSTOM_FIELDS_SAVED_EVENT, handleSaved);
    }, [refetch]);

    // Only show the counter for environments with a limit
    if (!usage || usage.limit === null || usage.limit === undefined) {
        return null;
    }

    // Determine the filled part of the pill and whether the limit is reached
    const limitReached = usage.count >= usage.limit;
    const fillPercentage = Math.min(100, Math.round((usage.count / usage.limit) * 100));

    // Offer the extended feature as upgrade when it is not active yet
    const showUpgradeBadge = !activeFeatures.includes('custom_fields_extended');

    return (
        <div className='pageheader-counter'>
            <div className='counter-meter'>
                <div className={`counter-pill ${limitReached ? 'limit-reached' : ''}`}>
                    <div className='counter-pill-fill' style={{ width: `${fillPercentage}%` }} />
                </div>
                <span className='counter-label'>
                    {t('custom_field.general.field_limit_counter', { amount: usage.count, limit: usage.limit })}
                </span>
            </div>
            {showUpgradeBadge && (
                <FeatureBadge feature='custom_fields_extended' />
            )}
        </div>
    );
};

export default CustomFieldUsageCounter;
